"use strict";

class RobustCart {
  constructor(rootEl, country, currency, cartUrl, cartAddUrl, cartChangeUrl) {
    this.rootEl = rootEl;
    this.country = country;
    this.currency = currency;
    this.cartUrl = cartUrl;
    this.cartAddUrl = cartAddUrl;
    this.cartChangeUrl = cartChangeUrl;
    this.scrollPosition = window.scrollY;

    this.log("Initializing");
    this.addAllListeners();
    this.log("Initialized");
    this.refreshCart();
    this.log("Initial cart refreshed!")
  }

  log(message) {
    if (window.ROBUST_CART_DEV) {
      console.log(
        `%cRobust Cart: ${message}`,
        "background: #cd302b; color: #fff",
        this
      );
    }
  }

  createAddToCartPromise(form) {    
    let formData = new FormData(form);
    let data = Object.fromEntries(formData.entries());

    const sellingPlanEl = document.getElementById('selling_plan')?.value || form.querySelector("input[name='selling_plan']")?.value;
    if(Boolean(sellingPlanEl) && sellingPlanEl != "undefined") {
      data.selling_plan = sellingPlanEl;
    } else {
      delete data.selling_plan;
    }
    
    return fetch(this.cartAddUrl, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });
  }

  createFetchCartPromise() {
    return fetch("/?section_id=robust-cart");
  }

  createUpdateCartItemPromise(id, quantity) {
    return fetch(this.cartChangeUrl, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ id, quantity }),
    });
  }

  createAddCartItemPromise(id, quantity) {
    return fetch(this.cartAddUrl, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ id, quantity }),
    });
  }

  formatMoney(cents) {
    const value = Number(cents) || 0;
    if (typeof Shopify !== "undefined" && typeof Shopify.formatMoney === "function") {
      return Shopify.formatMoney(value, window.theme?.moneyFormat || window.moneyFormat);
    }
    const currency = (this.currency && this.currency.active) || "USD";
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
    }).format(value / 100);
  }

  parseMoneyToCents(text) {
    if (!text) return null;
    const cleaned = String(text).replace(/[^0-9.]/g, "");
    if (!cleaned) return null;
    const n = parseFloat(cleaned);
    return Number.isNaN(n) ? null : Math.round(n * 100);
  }

  setPriceText(el, formatted) {
    if (!el) return;
    const hidden = el.querySelector(".visually-hidden");
    el.innerHTML = (hidden ? hidden.outerHTML : "") + formatted;
  }

  getSubtotalEl() {
    const row = this.rootEl
      .querySelector(".rc90-subtotal-label")
      ?.closest(".rc90-totals-row");
    return row?.querySelector(".rc90-totals-row-value") || null;
  }

  getShippingThreshold() {
    const wrapper = this.rootEl.querySelector(".progress-bar-wrapper");
    const fromData = parseInt(wrapper?.dataset.threshold || "", 10);
    return fromData > 0 ? fromData : 10000;
  }

  getItemDisplayPrices(itemEl, item) {
    const quantity = Number(item.quantity) || 1;
    const autoDiscountPercent =
      parseInt(itemEl?.dataset.autoDiscountPercent || "0", 10) || 0;
    const hasSellingPlan = !!item.selling_plan_allocation;
    const storedCompareUnit = parseInt(itemEl?.dataset.compareUnitPrice || "0", 10) || 0;
    const allocationCompareUnit = Number(item.selling_plan_allocation?.compare_at_price) || 0;
    const originalUnit = Number(item.original_price) || 0;
    const originalLine = Number(item.original_line_price) || 0;

    let current = Number(item.final_line_price) || 0;
    if (autoDiscountPercent > 0 && hasSellingPlan) {
      const custom =
        originalLine - Math.round((originalLine * autoDiscountPercent) / 100);
      if (custom > 0 && custom < current) current = custom;
    }

    let compare = originalLine;
    if (compare <= current) {
      const compareUnit = Math.max(
        storedCompareUnit,
        allocationCompareUnit,
        originalUnit
      );
      compare = compareUnit * quantity;
    }

    return { current, compare };
  }

  async postCartChange(payload) {
    const response = await fetch(this.cartChangeUrl, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.description || data.message || "Cart update failed");
    }
    return data;
  }

  normalizePlanId(value) {
    if (value === undefined) return undefined;
    if (value === null || value === "" || value === "null") return null;
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  }

  scheduleCartOp(lockId, op) {
    this._pendingOps = this._pendingOps || {};
    this._pendingOps[String(lockId)] = op;
    this.drainCartOps();
  }

  async drainCartOps() {
    if (this._drainingOps) return;
    this._drainingOps = true;
    let lastCart = null;

    try {
      while (this._pendingOps && Object.keys(this._pendingOps).length) {
        const lockId = Object.keys(this._pendingOps)[0];
        const op = this._pendingOps[lockId];
        delete this._pendingOps[lockId];
        try {
          lastCart = await this.executeCartOp(op);
        } catch (e) {
          console.error("Cart update failed:", e);
          try {
            lastCart = await getCart();
          } catch (syncError) {}
        }
      }
    } finally {
      this._drainingOps = false;
    }

    if (lastCart) {
      this.syncCartFromJson(lastCart);
    }

    if (this._pendingOps && Object.keys(this._pendingOps).length) {
      this.drainCartOps();
    }
  }

  async executeCartOp(op) {
    const itemEl =
      this.rootEl.querySelector(`.rc90-item[data-item-id="${op.variantId}"]`) ||
      (op.lineKey
        ? this.rootEl.querySelector(`[data-line-item-key="${op.lineKey}"]`)
        : null);
    const currentKey =
      itemEl?.getAttribute("data-line-item-key") || op.lineKey;
    const quantity =
      op.quantity != null
        ? Number(op.quantity)
        : Number(itemEl?.getAttribute("data-quantity")) || 1;

    if (op.type === "remove" || quantity < 1) {
      return this.postCartChange({ id: currentKey, quantity: 0 });
    }

    const payload = { id: currentKey, quantity };
    if (Object.prototype.hasOwnProperty.call(op, "sellingPlanId")) {
      const plan = this.normalizePlanId(op.sellingPlanId);
      payload.selling_plan = plan == null ? null : plan;
    }

    try {
      return await this.postCartChange(payload);
    } catch (e) {
      if (!Object.prototype.hasOwnProperty.call(op, "sellingPlanId")) {
        throw e;
      }
      await removeLineItem(currentKey);
      const plan = this.normalizePlanId(op.sellingPlanId);
      if (plan) {
        await addLineItemPlan(op.variantId, plan, quantity);
      } else {
        await addLineItem(op.variantId, quantity);
      }
      return getCart();
    }
  }

  renderItemPrices(itemEl, current, compare) {
    const pricesContainer = itemEl.querySelector(".rc90-item-main-footer-prices");
    if (!pricesContainer) return;

    let compareElement = pricesContainer.querySelector(
      ".rc90-item-main-footer-prices-compare"
    );
    if (compare > current) {
      if (!compareElement) {
        compareElement = document.createElement("span");
        compareElement.className = "rc90-item-main-footer-prices-compare";
        pricesContainer.insertBefore(compareElement, pricesContainer.firstChild);
      }
      this.setPriceText(compareElement, this.formatMoney(compare));
    } else if (compareElement) {
      compareElement.remove();
    }

    this.setPriceText(
      pricesContainer.querySelector(".rc90-item-main-footer-prices-price"),
      this.formatMoney(current)
    );
  }

  applyOptimisticQuantity(itemEl, newQty) {
    const oldQty =
      Number(itemEl.getAttribute("data-quantity")) ||
      Number(itemEl.querySelector(".rc90-item-main-footer-quantity-value")?.textContent) ||
      1;
    if (!oldQty || newQty === oldQty) return oldQty;

    itemEl.setAttribute("data-quantity", String(newQty));
    itemEl.querySelectorAll("[data-quantity]").forEach((el) => {
      el.setAttribute("data-quantity", String(newQty));
    });

    const qtyEl = itemEl.querySelector(".rc90-item-main-footer-quantity-value");
    if (qtyEl) {
      qtyEl.textContent = String(newQty);
      qtyEl.setAttribute("aria-label", `${newQty} in cart`);
    }

    return oldQty;
  }

  updateItemDom(itemEl, item) {
    itemEl.setAttribute("data-line-item-key", item.key);
    itemEl.setAttribute("data-quantity", String(item.quantity));
    itemEl.setAttribute("data-item-id", String(item.id));

    itemEl.querySelectorAll("[data-line-item-key]").forEach((el) => {
      el.setAttribute("data-line-item-key", item.key);
    });
    itemEl.querySelectorAll("[data-quantity]").forEach((el) => {
      el.setAttribute("data-quantity", String(item.quantity));
    });

    const qtyEl = itemEl.querySelector(".rc90-item-main-footer-quantity-value");
    if (qtyEl) {
      qtyEl.textContent = String(item.quantity);
      qtyEl.setAttribute("aria-label", `${item.quantity} in cart`);
    }

    const hasSellingPlan = !!item.selling_plan_allocation;
    const prices = this.getItemDisplayPrices(itemEl, item);
    this.renderItemPrices(itemEl, prices.current, prices.compare);

    const checkbox = itemEl.querySelector(".upsell__checkbox");
    if (checkbox) checkbox.checked = hasSellingPlan;

    const options = itemEl.querySelector(".upsell__options");
    if (options) {
      options.style.display = hasSellingPlan ? "block" : "none";
      const planId = item.selling_plan_allocation?.selling_plan?.id;
      if (hasSellingPlan && planId) {
        const planValue = String(planId);
        if ([...options.options].some((option) => option.value === planValue)) {
          options.value = planValue;
        }
      }
    }
  }

  updateTotalsDom(cart) {
    const subtotalEl = this.getSubtotalEl();
    if (subtotalEl) {
      this.setPriceText(subtotalEl, this.formatMoney(cart.total_price));
    }

    this.updateDiscountsRow(cart);

    if (typeof window.hfb068SyncCart === "function") {
      window.hfb068SyncCart(cart);
    }

    const hasSubscription = cart.items.some((item) => item.selling_plan_allocation);
    const freeMet = cart.total_price >= this.getShippingThreshold() || hasSubscription;
    const calculated = this.rootEl.querySelector(".rc90-totals--shipping .calculated");
    const free = this.rootEl.querySelector(".rc90-totals--shipping .free");
    if (calculated) calculated.classList.toggle("not-active", freeMet);
    if (free) free.classList.toggle("active", freeMet);
  }

  getCartDiscountTotal(cart) {
    const fromCart = Number(cart.total_discount || cart.total_discounts || 0);
    if (fromCart > 0) return fromCart;

    if (!Array.isArray(cart.items)) return 0;
    return cart.items.reduce((sum, item) => {
      const lineDiscount = Number(
        item.line_level_total_discount || item.total_discount || 0
      );
      const priceGap = Math.max(
        0,
        (Number(item.original_line_price) || 0) - (Number(item.final_line_price) || 0)
      );
      return sum + (lineDiscount || priceGap);
    }, 0);
  }

  updateDiscountsRow(cart) {
    const totals = this.rootEl.querySelector(".rc90-totals");
    if (!totals) return;

    const discountTotal = this.getCartDiscountTotal(cart);
    let discountsRow = totals.querySelector(".rc90-totals--discounts");
    if (discountTotal > 0) {
      if (!discountsRow) {
        const subtotalRow = this.rootEl
          .querySelector(".rc90-subtotal-label")
          ?.closest(".rc90-totals-row");
        discountsRow = document.createElement("div");
        discountsRow.className = "rc90-totals-row rc90-totals--discounts";
        const label = totals.dataset.discountsLabel || "Discounts";
        discountsRow.innerHTML =
          `<h3 class="rc90-totals-row-label">${label}</h3>` +
          `<span class="rc90-totals-row-value"></span>`;
        if (subtotalRow) {
          totals.insertBefore(discountsRow, subtotalRow);
        } else {
          totals.appendChild(discountsRow);
        }
      }
      discountsRow.hidden = false;
      discountsRow.removeAttribute("hidden");
      discountsRow.style.display = "flex";
      const valueEl = discountsRow.querySelector(".rc90-totals-row-value");
      if (valueEl) valueEl.textContent = this.formatMoney(discountTotal);
    } else if (discountsRow) {
      discountsRow.hidden = true;
      discountsRow.style.display = "none";
    }
  }

  updateShippingBarDom(cart) {
    const wrapper = this.rootEl.querySelector(".progress-bar-wrapper");
    if (!wrapper) return;

    const threshold = this.getShippingThreshold();
    const hasSubscription = cart.items.some((item) => item.selling_plan_allocation);
    const hasOnetime = cart.items.some((item) => !item.selling_plan_allocation);
    const allSub = hasSubscription && !hasOnetime;
    const percentRaw = threshold > 0 ? (cart.total_price * 100) / threshold : 100;
    const percent = Math.min(100, percentRaw);
    const freeMet = percent >= 100 || allSub;

    const bar = wrapper.querySelector(".free-shipping-bar");
    const progress = wrapper.querySelector(".progress-bar");
    const text = wrapper.querySelector(".free-shipping-text");

    if (bar) bar.classList.toggle("full-cart", freeMet);
    if (progress) progress.style.width = allSub ? "100%" : `${percent}%`;
    if (text) {
      text.classList.toggle("free-shipping-threshold-met", freeMet);
      const remaining = this.formatMoney(Math.max(0, threshold - cart.total_price));
      if (allSub) {
        text.innerHTML =
          '<span><span class="free-shipping-specific">FREE SHIPPING</span> included in your subscription!</span>';
      } else if (cart.total_price >= threshold || cart.total_price === 0) {
        text.innerHTML =
          '<span>🎉 Congrats! You’ve unlocked <span class="free-shipping-specific">FREE SHIPPING!</span></span>';
      } else if (percent > 65) {
        text.innerHTML = `<span>Almost there! <span class="money">${remaining}</span> left to get <span class="free-shipping-specific">FREE SHIPPING!</span></span>`;
      } else {
        text.innerHTML = `<span>Add <span class="money">${remaining}</span> more to get <span class="free-shipping-specific">FREE SHIPPING!</span></span>`;
      }
    }
  }

  syncCartFromJson(cart) {
    if (!cart || !Array.isArray(cart.items)) return;

    if (cart.items.length === 0) {
      this.refreshCart();
      return;
    }

    this.rootEl.querySelectorAll(".rc90-item[data-item-id]").forEach((itemEl) => {
      const variantId = itemEl.getAttribute("data-item-id");
      const lineKey = itemEl.getAttribute("data-line-item-key");
      const item =
        cart.items.find((i) => i.key === lineKey) ||
        cart.items.find((i) => String(i.id) === String(variantId));

      if (!item) {
        const li = itemEl.closest(".rc90-item-li");
        (li || itemEl).remove();
        return;
      }

      this.updateItemDom(itemEl, item);
    });

    this.updateTotalsDom(cart);
    this.updateShippingBarDom(cart);

    const jsonEl = this.rootEl.querySelector(".robust-cart-json");
    if (jsonEl) {
      jsonEl.setAttribute("data-cart-json", JSON.stringify(cart));
    }

    document.dispatchEvent(new CustomEvent("cart:updated", { detail: { cart } }));
  }

  async updateLineSellingPlan(lineItemKey, variantId, sellingPlanId) {
    this.scheduleCartOp(String(variantId || lineItemKey), {
      type: "plan",
      variantId,
      lineKey: lineItemKey,
      sellingPlanId,
    });
    return true;
  }

  openCart() {
    this.rootEl.classList.add("rc90--open");
    this.scrollPosition = window.scrollY;
    this.lockScroll();
    initAll();
    this.moveFocusToMiniCart();
  }

  moveFocusToMiniCart() {
    const tryFocus = (attemptsLeft) => {
      const cartDialog = this.rootEl.querySelector(".rc90-box.rc90-cart");
      if (!cartDialog) {
        if (attemptsLeft > 0) {
          window.setTimeout(() => tryFocus(attemptsLeft - 1), 80);
        }
        return;
      }

      const closeBtn = cartDialog.querySelector(".rc90-header-close");
      const heading = cartDialog.querySelector(".rc90-header-title");
      const target = closeBtn || heading || cartDialog;

      if (target === cartDialog && !cartDialog.hasAttribute("tabindex")) {
        cartDialog.setAttribute("tabindex", "-1");
      }

      try {
        target.focus({ preventScroll: false });
      } catch (e) {
        /* element not focusable yet */
      }
    };

    window.setTimeout(() => tryFocus(8), 120);
  }

  closeCart() {
    this.rootEl.classList.remove("rc90--open");
    this.unlockScroll();
    if (this._cartTrigger && typeof this._cartTrigger.focus === 'function') {
      this._cartTrigger.focus();
    }
  }

  async refreshCart() {
    let response = await this.createFetchCartPromise();
    let html = await response.text();
    let container = document.createElement("div");
    container.innerHTML = html;
    let content = container.querySelector(".rc90-box");
    this.rootEl.querySelector(".rc90-box").innerHTML = content.innerHTML;
    this.addInnerListeners();
    initAll();
    if (typeof window.hfb068SyncCart === "function") {
      const cart = this.getCartJson();
      if (cart && Array.isArray(cart.items)) window.hfb068SyncCart(cart);
    }
    try {
      if (simplyInsurance && typeof simplyInsurance !== "undefined")
        simplyInsurance.loadApp();
    } catch (e) {
      console.log(e);
    }

    // Restore focus to the quantity button that triggered this refresh
    if (this._pendingFocusVariantId && this._pendingFocusType) {
      const selector = `[data-item-id="${this._pendingFocusVariantId}"] .rc90-item-main-footer-quantity-${this._pendingFocusType}`;
      const target = this.rootEl.querySelector(selector);
      if (target) {
        target.focus({ preventScroll: true });
      }
      this._pendingFocusVariantId = null;
      this._pendingFocusType = null;
    }
  }

  async removeCartItem(id) {
    const cart = await this.postCartChange({ id, quantity: 0 });
    if (!cart.items || cart.items.length === 0) {
      await this.refreshCart();
      return;
    }
    this.syncCartFromJson(cart);
  }

  async changeCartItemQuantity(id, quantity) {
    if (quantity < 1) {
      await this.removeCartItem(id);
      return;
    }
    const cart = await this.postCartChange({ id, quantity });
    this.syncCartFromJson(cart);
  }

  getCartJson() {
    let dataEl = this.rootEl.querySelector(".robust-cart-json");
    if (dataEl) {
      let jsonData = dataEl.getAttribute("data-cart-json");
      if (jsonData) {
        return JSON.parse(jsonData);
      }
    }
  }

  addAllListeners() {
    this.addOpenCartListeners();
    this.addCartBoxListener();
    this.addInnerListeners();
    this.addLinkSubmissionListeners();
  }

  addInnerListeners() {
    this.addCloseCartListeners();
    this.addFormSubmissionListeners();
    this.addItemRemoveListeners();
    this.addItemQuantityListeners();
  }

  addCartBoxListener() {
    this.log("Adding cart box click listener");
    let box = this.rootEl.querySelector(".rc90-box");
    if (box) {
      box.addEventListener("click", (e) => {
        e.stopPropagation();
      });
    }
  }

  async addUpsellToCart(upsellProductId, quantity) {
    try {
      // Adding the upsell product to the cart via API.
      await this.createAddCartItemPromise(upsellProductId, quantity);

      // Refreshing the cart UI without page reload.
      await this.refreshCart();

      document
        .querySelector(`[data-item-id='${upsellProductId}'] .upsell__checkbox`)
        .click();
    } catch (error) {
      console.error("Error adding upsell to cart:", error);
      // Handle error (e.g., show a message to the user).
    }
  }

  addOpenCartListeners() {
    this.log("Adding open click listeners");
    document
      .querySelectorAll(`[href="${this.cartUrl}"], [href="/cart"]`)
      .forEach((element) => {
        element.addEventListener("click", (e) => {
          e.preventDefault();
          this._cartTrigger = element;
          this.openCart();
        });
      });
  }

  addCloseCartListeners() {
    this.log("Adding close click listeners");
    this.rootEl.addEventListener("click", this.closeCart.bind(this));
    this.rootEl
      .querySelectorAll(
        ".rc90-header-close, .rc90-buttons-continue-shopping, .rc90-empty-inner-continue"
      )
      .forEach((element) => {
        element.addEventListener("click", this.closeCart.bind(this));
      });
  }

  addLinkSubmissionListeners() {
    this.log("Adding link submission listeners");

    document.querySelectorAll('a[href*="/cart/add"]').forEach((link) => {
      link.addEventListener("click", async (e) => {
        e.preventDefault(); // Prevent the default link behavior
        this._cartTrigger = link;

        const urlParams = new URL(link.href).searchParams;
        const id = urlParams.get("id");
        const quantity = parseInt(urlParams.get("quantity")) || 1; // Default to 1 if quantity is not specified
        const sellingPlan = urlParams.get("selling_plan"); // Extract the selling_plan parameter

        console.log("sellingPlan", sellingPlan);

        if (id) {
          const data = { id, quantity };
          // If selling_plan parameter exists, add it to the data object
          if (sellingPlan) data.selling_plan = sellingPlan;

          await this.createAddCartItemPromiseWithData(data);
          await this.refreshCart();
          this.openCart();
        }
      });
    });
  }

  createAddCartItemPromiseWithData(data) {
    return fetch(this.cartAddUrl, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
      },
      body: JSON.stringify(data),
    });
  }

  addFormSubmissionListeners() {

    // Check if the event listener has already been added.
    if (!this.formSubmissionListenerAdded) {
      document.addEventListener(
        "click",
        async (e) => {
          if (
            e.target.matches(
              `form[action="${this.cartAddUrl}"] button[name="add"], form[action="${this.cartAddUrl}"] button[name="add"] *`
            )
          ) {
            e.preventDefault();
            this._cartTrigger = e.target.closest('button[name="add"]') || e.target;
            let form = e.target.closest(`form[action="${this.cartAddUrl}"]`);
            await this.createAddToCartPromise(form);
            await this.refreshCart();
            this.openCart();
          }
        },
        false
      );

      // Set a flag to indicate that the event listener has been added.
      this.formSubmissionListenerAdded = true;
    }
  }

  addItemRemoveListeners() {
    this.rootEl
      .querySelectorAll(".rc90-item-main-header-remove")
      .forEach((element) => {
        element.addEventListener("click", () => {
          const itemEl = element.closest(".rc90-item");
          const id = element.getAttribute("data-line-item-key");
          if (!id) return;
          const variantId = itemEl?.getAttribute("data-item-id");
          const li = itemEl?.closest(".rc90-item-li");
          if (li) li.hidden = true;
          this.scheduleCartOp(String(variantId || id), {
            type: "remove",
            variantId,
            lineKey: id,
            quantity: 0,
          });
        });
      });
  }

  addItemQuantityListeners() {
    this.rootEl
      .querySelectorAll(
        ".rc90-item-main-footer-quantity-plus, .rc90-item-main-footer-quantity-minus"
      )
      .forEach((element) => {
        if (element.dataset.qtyBound) return;
        element.dataset.qtyBound = "true";

        const isPlus = element.classList.contains(
          "rc90-item-main-footer-quantity-plus"
        );

        element.addEventListener("click", () => {
          const itemEl = element.closest(".rc90-item");
          if (!itemEl) return;

          const variantId = itemEl.getAttribute("data-item-id");
          const id =
            itemEl.getAttribute("data-line-item-key") ||
            element.getAttribute("data-line-item-key");
          const quantity = Number(
            itemEl.getAttribute("data-quantity") ||
              element.getAttribute("data-quantity")
          );

          if (!id || !quantity) return;

          const newQuantity = isPlus ? quantity + 1 : quantity - 1;
          const lockId = String(variantId || id);

          if (newQuantity < 1) {
            this.scheduleCartOp(lockId, {
              type: "remove",
              variantId,
              lineKey: id,
              quantity: 0,
            });
            return;
          }

          this.applyOptimisticQuantity(itemEl, newQuantity);
          this._qtyTimers = this._qtyTimers || {};
          this._qtyBurst = this._qtyBurst || {};
          clearTimeout(this._qtyTimers[lockId]);
          const delay = this._qtyBurst[lockId] ? 40 : 0;
          this._qtyBurst[lockId] = true;
          this._qtyTimers[lockId] = setTimeout(() => {
            this._qtyBurst[lockId] = false;
            this.scheduleCartOp(lockId, {
              type: "quantity",
              variantId,
              lineKey: itemEl.getAttribute("data-line-item-key") || id,
              quantity: Number(itemEl.getAttribute("data-quantity")) || newQuantity,
            });
          }, delay);
        });
      });
  }

  lockScroll() {
    document.body.style.overflow = "hidden";
    document.body.style.touchAction = "none";
  }

  unlockScroll() {
    document.body.style.overflow = "";
    document.body.style.touchAction = "";
  }
}

// Subscription Feature

document.addEventListener("DOMContentLoaded", initAll);

function initAll() {
  document.querySelectorAll(".rc90-item").forEach((item) => {
    const variantId = item.dataset.itemId;

    const sellingPlanCheckbox = item.querySelector(
      `.upsell__checkbox[data-variant-id="${variantId}"]`
    );
    const sellingPlanOptions = item.querySelector(`.upsell__options`);

    if (
      sellingPlanCheckbox &&
      sellingPlanOptions &&
      !item.dataset.listenerAdded
    ) {
      sellingPlanCheckbox.addEventListener(
        "change",
        handleCheckboxChange.bind(null, sellingPlanCheckbox, sellingPlanOptions)
      );
      sellingPlanOptions.addEventListener(
        "change",
        handleOptionsChange.bind(null, sellingPlanCheckbox, sellingPlanOptions)
      );

      item.dataset.listenerAdded = true;
    }
  });
}

async function handleCheckboxChange(sellingPlanCheckbox, sellingPlanOptions) {
  const variantId = sellingPlanCheckbox.dataset.variantId;
  const itemEl = sellingPlanCheckbox.closest(".rc90-item");
  const lineKey = itemEl?.getAttribute("data-line-item-key");
  const planId = sellingPlanCheckbox.checked
    ? sellingPlanOptions.options[0].value
    : null;

  sellingPlanOptions.style.display = sellingPlanCheckbox.checked ? "block" : "none";

  if (sellingPlanCheckbox.checked) {
    await addOrUpdateSellingPlan(variantId, planId, lineKey);
  } else {
    await removeSellingPlan(variantId, sellingPlanOptions.value, lineKey);
  }
}

async function handleOptionsChange(sellingPlanCheckbox, sellingPlanOptions) {
  const variantId = sellingPlanCheckbox.dataset.variantId;
  const itemEl = sellingPlanCheckbox.closest(".rc90-item");
  const lineKey = itemEl?.getAttribute("data-line-item-key");
  if (sellingPlanCheckbox.checked) {
    await addOrUpdateSellingPlan(variantId, sellingPlanOptions.value, lineKey);
  }
}

function updateFreeShippingBar(cart) {
  if (window.robustCart && typeof window.robustCart.updateShippingBarDom === "function" && cart) {
    window.robustCart.updateShippingBarDom(cart);
    return;
  }
}

async function addOrUpdateSellingPlan(variant, plan, lineKey) {
  if (window.robustCart && typeof window.robustCart.updateLineSellingPlan === "function") {
    const itemEl = document.querySelector(`.rc90-item[data-item-id="${variant}"]`);
    const key = lineKey || itemEl?.getAttribute("data-line-item-key");
    if (key) {
      return window.robustCart.updateLineSellingPlan(key, variant, plan);
    }
  }

  const cart = await getCart();
  const lineItemWithPlan = findLineItemWithPlan(cart, variant);
  const lineItemWithoutPlan = findLineItemWithoutPlan(cart, variant);
  let quantity;

  if (lineItemWithoutPlan) {
    quantity = lineItemWithoutPlan.quantity;
  } else if (lineItemWithPlan) {
    quantity = lineItemWithPlan.quantity;
  } else {
    quantity = 1;
  }

  if (lineItemWithPlan) await removeLineItem(lineItemWithPlan.key);
  if (lineItemWithoutPlan) await removeLineItem(lineItemWithoutPlan.key);
  const result = await addLineItemPlan(variant, plan, quantity);

  document.querySelectorAll(`[data-item-id="${variant}"]`).forEach((item) => {
    if (!item.querySelector(".rc90-item-main-upsell")) item.remove();
  });

  return result;
}

async function removeSellingPlan(variant, plan, lineKey) {
  if (window.robustCart && typeof window.robustCart.updateLineSellingPlan === "function") {
    const itemEl = document.querySelector(`.rc90-item[data-item-id="${variant}"]`);
    const key = lineKey || itemEl?.getAttribute("data-line-item-key");
    if (key) {
      return window.robustCart.updateLineSellingPlan(key, variant, null);
    }
  }

  const cart = await getCart();
  const lineItemWithPlan = findLineItemWithPlan(cart, variant);

  if (lineItemWithPlan) {
    await removeLineItem(lineItemWithPlan.key);
    await addLineItem(variant, lineItemWithPlan.quantity);
  }
}

async function getCart() {
  const result = await fetch("/cart.json");
  if (result.status === 200) return result.json();
  throw new Error(
    `Failed to get request, Shopify returned ${result.status} ${result.statusText}`
  );
}

function findLineItemWithPlan(cart, variant) {
  return cart.items.find(
    (item) => item.id === parseInt(variant) && item.selling_plan_allocation
  );
}

function findLineItemWithoutPlan(cart, variant) {
  return cart.items.find(
    (item) => item.id === parseInt(variant) && !item.selling_plan_allocation
  );
}

function removeLineItem(key) {
  return postToShopify("cart/change.js", {
    id: key,
    quantity: 0,
  });
}

function addLineItemPlan(variant, plan, quantity) {
  return postToShopify("cart/add.js", {
    items: [
      {
        quantity: quantity,
        id: variant,
        selling_plan: plan,
      },
    ],
  });
}

function addLineItem(variant, quantity) {
  return postToShopify("cart/add.js", {
    items: [
      {
        quantity: quantity,
        id: variant,
      },
    ],
  });
}

function postToShopify(endpoint, formData) {
  return fetch(window.Shopify.routes.root + endpoint, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(formData),
  })
    .then((response) => {
      if (!response.ok) throw new Error(response.statusText);
      return response.json();
    })
    .then((data) => {
      return data;
    })
}

async function updatePrice(variant) {
  const cart = await getCart();

  const item = cart.items.find((item) => item.id === parseInt(variant));

  let cartPrice = cart.total_price;
  let itemPrice = item.final_line_price;
  let originalItemPrice = item.original_line_price;

  const autoDiscountPercent = parseInt(
    document.querySelector(`[data-item-id="${variant}"]`)?.dataset.autoDiscountPercent || '0',
    10
  ) || 0;
  const hasSellingPlan = !!item.selling_plan_allocation;
  const shouldShowDiscount = autoDiscountPercent > 0 && hasSellingPlan;
  
  if (!shouldShowDiscount) {
    itemPrice = item.final_line_price;
    originalItemPrice = item.original_line_price;
  } else {
    const basePrice = item.original_line_price;
    const discountAmount = Math.round((basePrice * autoDiscountPercent) / 100);
    originalItemPrice = basePrice;
    itemPrice = basePrice - discountAmount;
  }

  const itemFormattedPrice = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(itemPrice / 100);

  const originalItemFormattedPrice = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(originalItemPrice / 100);

  const cartFormattedPrice = new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(cartPrice / 100);

  const pricesContainer = document.querySelector(
    `[data-item-id="${variant}"] .rc90-item-main-footer-prices`
  );
  
  if (pricesContainer) {
    let compareElement = pricesContainer.querySelector('.rc90-item-main-footer-prices-compare');
    
    const hasRegularComparePrice = originalItemPrice > itemPrice;
    const shouldShowComparePrice = shouldShowDiscount || hasRegularComparePrice;
    
    if (shouldShowComparePrice) {
      if (!compareElement) {
        compareElement = document.createElement('span');
        compareElement.className = 'rc90-item-main-footer-prices-compare';
        pricesContainer.insertBefore(compareElement, pricesContainer.firstChild);
      }
      compareElement.innerText = originalItemFormattedPrice;
    } else {
      if (compareElement) {
        compareElement.remove();
      }
    }
    
    const priceElement = pricesContainer.querySelector('.rc90-item-main-footer-prices-price');
    if (priceElement) {
      priceElement.innerText = itemFormattedPrice;
    }
  }

  document.querySelector(
    ".rc90-totals-row:last-child .rc90-totals-row-value"
  ).innerText = cartFormattedPrice;
  
  // Update quantity button attributes with new line item key
  const itemElement = document.querySelector(`[data-item-id="${variant}"]`);
  if (itemElement) {
    const plusButton = itemElement.querySelector('.rc90-item-main-footer-quantity-plus');
    const minusButton = itemElement.querySelector('.rc90-item-main-footer-quantity-minus');
    const quantityDisplay = itemElement.querySelector('.rc90-item-main-footer-quantity-value');
    
    if (plusButton) {
      plusButton.setAttribute('data-line-item-key', item.key);
      plusButton.setAttribute('data-quantity', item.quantity);
    }
    
    if (minusButton) {
      minusButton.setAttribute('data-line-item-key', item.key);
      minusButton.setAttribute('data-quantity', item.quantity);
    }
    
    if (quantityDisplay) {
      quantityDisplay.textContent = item.quantity;
    }
  }
}

function showOverlay() {
  var overlay = document.getElementById("cart-overlay");
  var cartBox = document.querySelector(".rc90-box");
  if (overlay && cartBox) {
    overlay.style.height = cartBox.scrollHeight + "px";
    overlay.style.display = "flex";
  }
}

function hideOverlay() {
  document.getElementById("cart-overlay").style.display = "none";
}
